import * as React from 'react'
import { styled } from '@design/pt-ui'
import CollapsedItem from './CollapsedItem'
import { BreadcrumbItemProps } from './BreadcrumbItem'

const HEIGHT = 24
const DEFAULT_MAX_ITEMS = 5

const Root = styled('div')({
  alignItems: 'center',
  display: 'flex',
  flexWrap: 'wrap',
})

const Separator = styled('div')(({ theme }) => ({
  color: theme.palette.grey[400],
  display: 'flex',
  height: HEIGHT,
  lineHeight: `${HEIGHT}px`,
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
  paddingLeft: theme.spacing(0.25),
  paddingRight: theme.spacing(0.25),
  userSelect: 'none',
  alignItems: 'center',
  justifyContent: 'center',
}))

interface BreadcrumbProps {
  children: React.ReactNode
  itemsAfterCollapse?: number
  itemsBeforeCollapse?: number
  maxItems?: number
  separator?: string | React.ReactNode
  separatorText: string | React.ReactNode
}

function Breadcrumb(props: BreadcrumbProps) {
  const [state, setState] = React.useState({ expanded: false })
  const expand = () => {
    setState({ expanded: true })
  }

  const renderAllItems = () => {
    return (
      React.Children.map(props.children, (child, index) =>
        React.cloneElement(child as React.ReactElement<BreadcrumbItemProps>, {
          key: index,
        }),
      ) || []
    )
  }

  const renderItemsBeforeAndAfter = () => {
    const { itemsBeforeCollapse, itemsAfterCollapse } = props

    const allItems = renderAllItems()
    if (
      (itemsBeforeCollapse || 1) + (itemsAfterCollapse || 1) >=
      allItems.length
    ) {
      return allItems
    }

    const beforeItems = allItems.slice(0, itemsBeforeCollapse || 1)
    const afterItems = allItems.slice(
      allItems.length - (itemsAfterCollapse || 1),
      allItems.length,
    )

    return [
      ...beforeItems,
      <CollapsedItem key="ellipsis" onClick={expand} />,
      ...afterItems,
    ]
  }

  const insertSeparators = (items: any[]) => {
    const { separatorText } = props

    return items.reduce((arr, v, i, source) => {
      return i < source.length - 1
        ? arr.concat(
            v,
            <Separator key={`separator-${i}`}>{separatorText}</Separator>,
          )
        : arr.concat(v)
    }, [])
  }

  const {
    children,
    maxItems,
    itemsBeforeCollapse,
    itemsAfterCollapse,
    separator,
    separatorText,
    ...rest
  } = props

  if (!children) {
    return <Root {...rest} />
  }

  return (
    <Root {...rest}>
      {state.expanded ||
      ((maxItems || DEFAULT_MAX_ITEMS) &&
        renderAllItems().length <= (maxItems || DEFAULT_MAX_ITEMS))
        ? insertSeparators(renderAllItems())
        : insertSeparators(renderItemsBeforeAndAfter())}
    </Root>
  )
}

export default Breadcrumb
